import React, { useEffect, useRef, useState } from 'react';
import Axios from 'axios';
import I18n from 'i18n-js';
import { tv } from 'tailwind-variants';
import * as Routes from '@/modules/routes';
import styles from '@/components/shared/LessonOverview/Cards/Cards.module.scss';
import { CARDS_ROW_REFLOW } from '@/components/shared/LessonOverview/Cards/reflowStyles';
import { Lesson, Program, SectionType, StafferSlideShows, StudentSlideShows } from '@/types/api/LessonOverview';
import useFlags from '@/hooks/useFlags';
import SlideTypeToggle from './Staffer/SlideTypeToggle';
import StafferFlexibleLessonOptions from './Staffer/FlexibleLessonOptions';
import StudentFlexibleLessonOptions from './Student/FlexibleLessonOptions';
import TeacherSlideShows from './Staffer/TeacherSlideShows';
import lessonOverviewStyles from './Cards/index.module.scss';
import LessonSubheader from './LessonSubheader';
import DiveDeeper from './DiveDeeper';
import AssessWith from './AssessWith';

interface LessonOverviewProps {
  hasAssessments?: boolean;
  hideTabs?: boolean;
  isStaffer: boolean;
  lessonId?: string;
  locale?: string;
  programId?: string;
  stafferId?: number;
}

type APIResponse = {
  lesson: Lesson;
  program: Program;
  sections: SectionType[];
  slideShows: StafferSlideShows | StudentSlideShows;
  teacherInitials: string;
};

const lessonOverviewVariants = tv({
  slots: {
    cards: styles.cards,
    wrapper: lessonOverviewStyles.lessonOverviewWrapper,
  },
  variants: {
    reflow: {
      true: {
        cards: CARDS_ROW_REFLOW,
        wrapper: 'tw-flex tw-flex-col tw-box-border tw-w-auto',
      },
    },
  },
});

const LessonOverview = ({
  hasAssessments = true,
  hideTabs = false,
  isStaffer,
  lessonId = null,
  locale = null,
  programId = null,
  stafferId = null,
}: LessonOverviewProps) => {
  const [selected, setSelected] = useState<string | number>('resource-0');
  const [showArchived, setShowArchived] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lesson, setLesson] = useState(null);
  const [program, setProgram] = useState(null);
  const [sections, setSections] = useState([]);
  const [slideShows, setSlideShows] = useState(null);
  const showTeacherLessonGuides = selected !== 'resource-0';
  const FlexibleLessonOptions = isStaffer ? StafferFlexibleLessonOptions : StudentFlexibleLessonOptions;
  const { accessibilityReflow } = useFlags();
  const { cards, wrapper } = lessonOverviewVariants({ reflow: accessibilityReflow });

  const ajaxPath = Routes.plato_api_program_lesson_overview_index_path(
    programId, lessonId, { locale },
  );

  const getSlideShows = () => {
    Axios.get<APIResponse>(ajaxPath)
      .then((response) => {
        setLesson(response.data.lesson);
        setProgram(response.data.program);
        setSections(response.data.sections);
        setSlideShows(response.data.slideShows);
        setIsLoading(false);
      });
  };

  const isInitialSelection = useRef(true);

  useEffect(() => {
    getSlideShows();
  }, []);

  // Move focus to the panel when the user switches TCI Content / My Slideshows (not on
  // initial mount) so screen-reader users are taken to the newly shown content.
  useEffect(() => {
    if (isInitialSelection.current) {
      isInitialSelection.current = false;
      return;
    }

    const panelId = selected === 'resource-0' ? 'tciContentContainer' : 'mySlideshowsContainer';
    document.getElementById(panelId)?.focus();
  }, [selected]);

  if (isLoading) {
    return (
      <div className="mt20">
        <i aria-hidden="true" className="fa fa-spinner fa-spin" />
        <span className="ml5">Lesson guides are currently loading. Please wait...</span>
      </div>
    );
  }

  const hasArchivedContent = () => (
    slideShows?.tci?.first?.archived
      || slideShows?.tci?.oldSlideShows?.some(slideShow => slideShow.archived)
      || slideShows?.tci?.newSlideShows?.some(slideShow => slideShow.archived)
  );

  return (
    <div>
      {!hideTabs && stafferId && (
        <SlideTypeToggle
          hasArchivedContent={hasArchivedContent()}
          hideTabs={hideTabs}
          setSelected={setSelected}
          toggleShowArchived={() => setShowArchived(prev => !prev)}
        />
      )}
      <div className={cards()}>
        {showTeacherLessonGuides && stafferId && (
          <TeacherSlideShows
            hideTabs={hideTabs}
            lessonId={lesson.id}
            programId={program.id}
            slideShows={slideShows}
            stafferId={stafferId}
          />
        )}
        {!showTeacherLessonGuides && (
          <div
            aria-label={I18n.t('tci_content')}
            className={wrapper()}
            id="tciContentContainer"
            role="region"
            tabIndex={-1}
          >
            <LessonSubheader
              description={I18n.t('lesson_subheader_flexible_lesson_options')}
              isStaffer={isStaffer}
              title={I18n.t('flexible_lesson_options')}
            >
              <FlexibleLessonOptions
                hideTabs={hideTabs}
                lesson={lesson}
                program={program}
                sections={sections}
                showArchived={showArchived}
                slideShows={slideShows}
                stafferId={stafferId}
              />
            </LessonSubheader>
            <DiveDeeper
              lesson={lesson}
              program={program}
              sections={sections}
              stafferId={stafferId}
            />
            <AssessWith
              hasAssessments={hasAssessments}
              lesson={lesson}
              program={program}
              sections={sections}
              stafferId={stafferId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonOverview;
